import type { AnalyticsUsageHoursDto } from '@pams/shared';
import {
  DAYTIME_FROM_HOUR,
  DAYTIME_TO_HOUR,
  HEAT_LEVEL_CLASSES,
  readUsageHours,
  type UsageHoursSpan,
} from '@/lib/usageHours';
import { cn } from '@/lib/utils';
import { USAGE_ANALYTICS_COPY } from '../copy';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/** Hour ticks under the grid. Every sixth hour, so the labels never collide at the floor width. */
const TICKS = new Set([0, 6, 12, 18]);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHEN THE SYSTEM IS USED — seven IST weekdays × twenty-four IST hours. [ANALYTICS §5]
 *
 *  ⚠️ R2, THREE TIMES OVER. The grid is `aria-hidden`; a visible SENTENCE names the busiest hour
 *  and the daytime share, so a sighted colour-blind reader is told what the darkest cell means;
 *  and an `sr-only` TABLE carries every cell, days by hours, for whoever wants the numbers.
 *
 *  ⚠️ THE GRID TEMPLATE IS ONE LITERAL CLASS — a 3rem day column then twenty-four equal hours.
 *  Tailwind extracts candidates from source text, so the column count may not be built from a
 *  number, and each cell's shade comes from `HEAT_LEVEL_CLASSES` by index for the same reason.
 *  `data-level` carries the level itself, which is what a test reads — jsdom resolves no class.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const UsageHoursGrid = ({
  data,
  wholeWindow,
  span,
}: {
  data: AnalyticsUsageHoursDto;
  wholeWindow: boolean;
  span: UsageHoursSpan;
}) => {
  const copy = USAGE_ANALYTICS_COPY.usageHours;
  const reading = readUsageHours(data.cells, data.datesPerWeekday, wholeWindow, span);

  return (
    /* ⚠️ `relative`, because the `sr-only` table is `position: absolute` — `Loading`'s [D4] reason. */
    <div className="relative flex min-w-0 flex-col gap-3" data-testid="usage-hours">
      <p className="text-xs text-muted-foreground">{copy.basis[reading.basis]}</p>

      <div aria-hidden data-testid="usage-hours-grid" className="flex flex-col gap-1">
        <div className="grid grid-cols-[3rem_repeat(24,minmax(0,1fr))] gap-px">
          {reading.levels.map((row, weekday) => (
            <div key={copy.weekdaysShort[weekday]} className="contents">
              <span className="pr-2 text-xs leading-5 text-muted-foreground">{copy.weekdaysShort[weekday]}</span>
              {row.map((level, hour) => (
                <span
                  key={hour}
                  data-level={level}
                  data-cell={`${String(weekday)}-${String(hour)}`}
                  className={cn('h-5 rounded-sm', HEAT_LEVEL_CLASSES[level])}
                />
              ))}
            </div>
          ))}
          <span />
          {HOURS.map((hour) => (
            <span key={hour} className="text-[10px] leading-4 text-muted-foreground">
              {TICKS.has(hour) ? copy.hourTick(hour) : ''}
            </span>
          ))}
        </div>
      </div>

      {reading.busiest === null ? (
        <p className="text-sm text-foreground">{copy.none}</p>
      ) : (
        <p className="text-sm text-foreground" data-testid="usage-hours-sentence">
          {copy.busiest(reading.basis, reading.busiest.weekday, reading.busiest.hour, reading.busiest.value)}{' '}
          {reading.daytimeShare === null
            ? null
            : copy.daytime(Math.round(reading.daytimeShare * 100), DAYTIME_FROM_HOUR, DAYTIME_TO_HOUR)}
        </p>
      )}

      <table className="sr-only" data-testid="usage-hours-table">
        <caption>{copy.tableCaption(reading.basis)}</caption>
        <thead>
          <tr>
            <th scope="col">{copy.dayHeader}</th>
            {HOURS.map((hour) => (
              <th key={hour} scope="col">
                {copy.hourHeader(hour)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reading.values.map((row, weekday) => (
            <tr key={copy.weekdaysShort[weekday]}>
              <th scope="row">{copy.weekday(weekday)}</th>
              {row.map((value, hour) => (
                <td key={hour}>{copy.cellFigure(reading.basis, value)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
